import { Page, Prisma } from 'skedyul-db'
import { canAccessPage } from './canAccessPage'

export const INSTANCE_ID_PARAM = '[id]'

export function materializeInstancePath(
  templatePath: string,
  instanceId: string,
): string {
  const normalized = templatePath.replace(/^\//, '')
  if (normalized.includes(INSTANCE_ID_PARAM)) {
    return normalized.replace(INSTANCE_ID_PARAM, instanceId)
  }
  return `${normalized}/${instanceId}`
}

export function buildWorkplacePageHref(props: {
  subdomain: string
  pagePath: string
  search?: URLSearchParams | Record<string, string | undefined>
}): string {
  const path = props.pagePath.replace(/^\//, '')
  let search = ''
  if (props.search instanceof URLSearchParams) {
    search = props.search.toString()
  } else if (props.search) {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(props.search)) {
      if (value != null && value !== '') {
        params.set(key, value)
      }
    }
    search = params.toString()
  }
  const base = `/${props.subdomain}/${path}`
  return search ? `${base}?${search}` : base
}

export async function resolveInstancePagePath(
  props: { modelId: string; workplaceId: string },
  tx: Prisma.TransactionClient,
): Promise<string | null> {
  const instancePage = await tx.page.findFirst({
    where: {
      modelId: props.modelId,
      workplaceId: props.workplaceId,
      type: 'INSTANCE',
    },
    select: { path: true },
  })
  return instancePage?.path ?? null
}

export async function resolveDefaultListPagePath(
  props: {
    modelId: string
    workplaceId: string
    memberId?: string | null
    fromPath?: string
    parentPage?: Page | null
  },
  tx: Prisma.TransactionClient,
): Promise<string | null> {
  const { modelId, workplaceId, memberId, fromPath, parentPage } = props

  if (fromPath) {
    const fromPage = await tx.page.findFirst({
      where: {
        workplaceId,
        path: fromPath,
        modelId,
        type: 'LIST',
      },
    })
    if (fromPage && canAccessPage(fromPage, memberId)) {
      return fromPage.path
    }
  }

  if (parentPage?.type === 'LIST' && canAccessPage(parentPage, memberId)) {
    return parentPage.path
  }

  const defaultList = await tx.page.findFirst({
    where: {
      workplaceId,
      modelId,
      type: 'LIST',
      isDefault: true,
      OR: [{ memberId: null }, ...(memberId ? [{ memberId }] : [])],
    },
    orderBy: { createdAt: 'asc' },
    select: { path: true },
  })

  if (defaultList && canAccessPage(defaultList as Page, memberId)) {
    return defaultList.path
  }

  const anyList = await tx.page.findFirst({
    where: {
      workplaceId,
      modelId,
      type: 'LIST',
      OR: [{ memberId: null }, ...(memberId ? [{ memberId }] : [])],
    },
    orderBy: { createdAt: 'asc' },
    select: { path: true },
  })

  return anyList?.path ?? parentPage?.path ?? null
}

export async function resolveInstancePagePathWithFallback(
  props: {
    modelId: string
    workplaceId: string
    handlePlural: string
  },
  tx: Prisma.TransactionClient,
): Promise<string> {
  const resolved = await resolveInstancePagePath(
    { modelId: props.modelId, workplaceId: props.workplaceId },
    tx,
  )
  return resolved ?? `${props.handlePlural}/${INSTANCE_ID_PARAM}`
}
